import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Layout from '../components/Layout';
import StatusChip, { RenewalChip, TierChip } from '../components/StatusChip';
import { bands, renewalStatuses } from '../theme';
import api from '../services/api';
import { dayOf } from '../utils/dates';
import { useI18n } from '../i18n';
import { useCan } from '../hooks/useMe';

function Stat({ label, value, hint }) {
  return (
    <Box sx={{ backgroundColor: '#fff', px: 2, py: 1.5, flex: '1 1 150px' }}>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 500, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: '#847782',
      }}>{label}</Typography>
      <Typography sx={{ fontSize: 23, fontWeight: 600, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
        {value}
        {hint && <Box component="span" sx={{ fontSize: 12, fontWeight: 400, color: '#847782', ml: 0.7 }}>{hint}</Box>}
      </Typography>
    </Box>
  );
}

const rateColor = (pct) => (pct === null ? '#847782'
  : pct >= 80 ? bands.healthy.color
  : pct >= 50 ? bands.watch.color
  : bands.at_risk.color);

// Tasa de renovación del mes en curso y los dos anteriores: de lo que estaba
// programado en el mes, cuánto se renovó. Cada mes abre la lista de sus
// cuentas. El KPI sólo existe para quien ve el libro completo; el backend
// manda null al resto.
function RenewalRate({ kpi, onOpenMonth }) {
  const { t, locale } = useI18n();
  const monthLabel = (month) => dayOf(`${month}-01`)
    .toLocaleDateString(locale, { month: 'short' }).replace('.', '');

  return (
    <Box sx={{ backgroundColor: '#fff', px: 2, py: 1.5, flex: '1 1 300px' }}>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 500, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: '#847782',
      }}>{t('dashboard.stats.renewalRate')}</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mt: 0.4 }}>
        {kpi.months.map((m) => (
          <Box
            key={m.month}
            onClick={() => onOpenMonth(m)}
            sx={{
              cursor: 'pointer', borderRadius: '4px', px: 0.8, py: 0.4, mx: -0.8,
              '&:hover': { backgroundColor: '#FFF0F0' },
            }}
          >
            <Typography sx={{ fontSize: 11, color: '#847782', textTransform: 'capitalize' }}>
              {monthLabel(m.month)}
            </Typography>
            <Typography sx={{
              fontSize: 20, fontWeight: 600, lineHeight: 1.2,
              fontVariantNumeric: 'tabular-nums', color: rateColor(m.pct),
            }}>
              {m.pct === null ? t('common.dash') : `${m.pct}%`}
            </Typography>
            <Box sx={{ height: 4, borderRadius: 3, backgroundColor: '#EDE6E3', overflow: 'hidden', my: 0.4 }}>
              <Box sx={{ width: `${m.pct || 0}%`, height: '100%', backgroundColor: rateColor(m.pct) }} />
            </Box>
            <Typography sx={{ fontSize: 11, color: '#847782', fontVariantNumeric: 'tabular-nums' }}>
              {m.due
                ? t('dashboard.stats.renewalRateOf', { renewed: m.renewed, due: m.due })
                : t('dashboard.stats.renewalRateNone')}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// Las cuentas que tenían renovación programada en el mes: las renovadas con
// su fecha de confirmación y las que no, con su estatus actual.
function RenewalMonthDialog({ month, onClose, onOpen }) {
  const { t, fmtDate, fmtMonthYear } = useI18n();
  if (!month) return null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ textTransform: 'capitalize' }}>
          {t('dashboard.renewalMonth.title', { month: fmtMonthYear(dayOf(`${month.month}-01`)) })}
        </Box>
        <Typography sx={{ fontSize: 13, color: '#847782' }}>
          {t('dashboard.renewalMonth.summary', { renewed: month.renewed, due: month.due })}
          {month.pct !== null && (
            <Box component="span" sx={{ ml: 0.8, fontWeight: 600, color: rateColor(month.pct) }}>
              {month.pct}%
            </Box>
          )}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 0, pb: 1 }}>
        {month.accounts.length === 0 && (
          <Box sx={{ px: 3, py: 2, color: '#847782', fontSize: 14 }}>
            {t('dashboard.renewalMonth.empty')}
          </Box>
        )}
        {month.accounts.map((a) => (
          <Box
            key={`${a.source_db}:${a.tenant_id}:${a.due_date}`}
            onClick={() => onOpen(a)}
            sx={{
              display: 'grid', gridTemplateColumns: '1fr auto', gap: 1,
              px: 3, py: 1.2, alignItems: 'center', cursor: 'pointer',
              borderBottom: '1px solid #F0ECEE',
              '&:hover': { backgroundColor: '#FFF0F0' },
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{a.organization_name}</Typography>
                <TierChip tier={a.plan} />
              </Box>
              <Typography sx={{ fontSize: 11.5, color: '#847782', mt: 0.2 }}>
                {a.cs_owner || t('common.unassigned')}
                {a.billing_frequency && ` · ${t(`billingFrequency.${a.billing_frequency}`)}`}
                {' · '}{t('dashboard.renewalMonth.due', { date: fmtDate(dayOf(a.due_date)) })}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              {a.status === 'renewed' ? (
                <>
                  <Chip
                    label={t('dashboard.renewalMonth.renewed')}
                    size="small"
                    sx={{ backgroundColor: renewalStatuses.confirmed.color, color: '#fff', fontWeight: 600, fontSize: 11 }}
                  />
                  <Typography sx={{ fontSize: 11, color: '#847782', mt: 0.3 }}>
                    {t('dashboard.renewalMonth.renewedOn', { date: fmtDate(dayOf(a.renewed_on)) })}
                  </Typography>
                </>
              ) : (
                <RenewalChip status={a.status} />
              )}
            </Box>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
}

// Cierre de mes de renovaciones (sólo administración). Las cuentas confirmadas
// se quedan en «confirmada» todo su mes; este cierre pasa al historial las de
// meses ya terminados, corre su siguiente fecha y deja a la vista cuáles
// vencieron sin renovarse.
function CloseMonthDialog({ open, onClose, onClosed, onOpen }) {
  const { t, fmtDate } = useI18n();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setResult(null); setError(null); }
  }, [open]);

  async function run() {
    setRunning(true);
    setError(null);
    try {
      const { data } = await api.post('/api/dashboard/close-renewals');
      setResult(data);
      onClosed();
    } catch (err) {
      setError(err.response?.data?.error || t('dashboard.closeMonth.error'));
    } finally {
      setRunning(false);
    }
  }

  const section = (title, list, detail, color) => list.length > 0 && (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{
        px: 3, pb: 0.6, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em',
        textTransform: 'uppercase', color,
      }}>{title} · {list.length}</Typography>
      {list.map((a) => (
        <Box
          key={`${a.source_db}:${a.tenant_id}:${a.due_date}`}
          onClick={() => onOpen(a)}
          sx={{
            display: 'flex', justifyContent: 'space-between', gap: 1,
            px: 3, py: 0.9, cursor: 'pointer', borderBottom: '1px solid #F0ECEE',
            '&:hover': { backgroundColor: '#FFF0F0' },
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{a.organization_name}</Typography>
          <Typography sx={{ fontSize: 12, color: '#847782', whiteSpace: 'nowrap' }}>{detail(a)}</Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>{t('dashboard.closeMonth.title')}</DialogTitle>
      <DialogContent sx={{ px: 0, pb: 1 }}>
        {error && <Alert severity="error" sx={{ mx: 3, mb: 1 }}>{error}</Alert>}

        {!result && (
          <Typography sx={{ px: 3, fontSize: 14, color: '#4A3F47' }}>
            {t('dashboard.closeMonth.intro')}
          </Typography>
        )}

        {result && (
          <>
            <Typography sx={{ px: 3, fontSize: 14 }}>
              {t('dashboard.closeMonth.summary', {
                closed: result.stats.closed, notRenewed: result.stats.not_renewed,
                date: fmtDate(dayOf(result.closed_before)),
              })}
            </Typography>
            {result.stats.closed + result.stats.not_renewed + result.stats.missing_frequency === 0 && (
              <Typography sx={{ px: 3, mt: 1.5, fontSize: 13.5, color: '#847782' }}>
                {t('dashboard.closeMonth.nothing')}
              </Typography>
            )}
            {section(t('dashboard.closeMonth.notRenewed'), result.not_renewed,
              (a) => (a.due_date ? t('dashboard.renewalMonth.due', { date: fmtDate(dayOf(a.due_date)) }) : ''),
              '#AE5B61')}
            {section(t('dashboard.closeMonth.missingFrequency'), result.missing_frequency,
              () => t('dashboard.closeMonth.missingFrequencyHint'), '#C9873F')}
            {section(t('dashboard.closeMonth.closed'), result.closed,
              (a) => t('dashboard.closeMonth.next', { date: fmtDate(dayOf(a.next_renewal_date)) }),
              renewalStatuses.confirmed.color)}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running} sx={{ color: '#847782' }}>
          {t('common.close')}
        </Button>
        {!result && (
          <Button variant="contained" onClick={run} disabled={running}>
            {running ? t('dashboard.closeMonth.running') : t('dashboard.closeMonth.confirm')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function CardHeader({ title, count, action }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2, px: 1.8, py: 1.4,
      borderBottom: '1px solid #E4E0E2',
      fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: '#847782',
    }}>
      {title}
      {count != null && <Box component="span" sx={{ color: '#FF6666', fontSize: 13 }}>{count}</Box>}
      {action && <Box sx={{ ml: 'auto', fontSize: 11.5, color: '#FF6666', textTransform: 'none', letterSpacing: 0 }}>{action}</Box>}
    </Box>
  );
}

const tag = (label, bg, color) => (
  <Box component="span" sx={{
    fontSize: 10.5, fontWeight: 600, px: 0.8, py: '2px', borderRadius: '3px',
    backgroundColor: bg, color, whiteSpace: 'nowrap',
  }}>{label}</Box>
);

// Contactos buscados en los últimos 30 días con su seguimiento: si
// respondieron y si agendaron sesión. Lo que no contestó queda a la vista para
// volver a intentarlo.
function ContactedCard({ contacted, showOwner, onOpen }) {
  const { t, fmtDate } = useI18n();

  // Mitad de la pantalla como máximo: la lista crece rápido en un mes y no debe
  // empujar todo lo demás hacia abajo. El encabezado se queda fijo.
  return (
    <Paper sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '50vh' }}>
      <CardHeader
        title={t('dashboard.contacted.title')}
        count={contacted.length}
        action={t('dashboard.contacted.action')}
      />
      <Box sx={{ overflowY: 'auto', minHeight: 0 }}>
        {contacted.length === 0 && (
          <Box sx={{ p: 3, color: '#847782', fontSize: 14 }}>{t('dashboard.contacted.empty')}</Box>
        )}
        {contacted.map((c) => (
          <Box
            key={c.id}
            onClick={() => onOpen(c)}
            sx={{
              display: 'grid', gridTemplateColumns: '1fr auto', gap: 1,
              px: 1.8, py: 1.2, alignItems: 'start', cursor: 'pointer',
              borderBottom: '1px solid #F0ECEE', '&:last-of-type': { borderBottom: 'none' },
              '&:hover': { backgroundColor: '#FFF0F0' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
                  {c.name || c.email || c.whatsapp}
                </Typography>
                {c.job_title && (
                  <Typography sx={{ fontSize: 11.5, color: '#847782' }}>{c.job_title}</Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: 11.5, color: '#847782', mt: 0.2 }}>
                {c.account_name}
                {showOwner ? ` · ${c.cs_owner || t('common.unassigned').toLowerCase()}` : ''}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: 12, color: '#847782', fontVariantNumeric: 'tabular-nums', mb: 0.4 }}>
                {fmtDate(dayOf(c.last_contacted_at))}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                {c.responded === true && tag(t('dashboard.contacted.responded'), '#E6EFE9', '#4F7A5F')}
                {c.responded === false && tag(t('dashboard.contacted.noResponse'), '#F6E7E8', '#AE5B61')}
                {c.responded == null && tag(t('dashboard.contacted.waiting'), '#EDE6E3', '#6B5F68')}
                {c.session_scheduled && tag(t('dashboard.contacted.session'), '#F3E9F3', '#531253')}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

// Renovaciones de los próximos 90 días, agrupadas por mes. Es la misma
// tarjeta en el tablero de CS y en el de ventas.
function RenewalsCard({ renewals, onOpen }) {
  const { t, fmtDate, fmtMonthYear } = useI18n();
  let lastMonth = null;

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <CardHeader
        title={t('dashboard.renewals.title')}
        count={renewals.length}
        action={t('dashboard.renewals.action')}
      />
      {renewals.length === 0 && (
        <Box sx={{ p: 3, color: '#847782', fontSize: 14 }}>
          {t('dashboard.renewals.empty')}
        </Box>
      )}
      {renewals.map((r) => {
        const month = fmtMonthYear(dayOf(r.renewal_date));
        const isNew = month !== lastMonth;
        lastMonth = month;
        return (
          <Box key={`${r.source_db}:${r.tenant_id}`}>
            {isNew && (
              <Box sx={{
                px: 1.8, pt: 1.2, pb: 0.6, backgroundColor: '#F5F5F5',
                borderBottom: '1px solid #F0ECEE', fontSize: 11, fontWeight: 600,
                letterSpacing: '0.1em', textTransform: 'uppercase', color: '#847782',
              }}>{month}</Box>
            )}
            <Box
              onClick={() => onOpen(r)}
              sx={{
                display: 'grid', gridTemplateColumns: '1fr auto', gap: 1,
                px: 1.8, py: 1.4, alignItems: 'center', cursor: 'pointer',
                borderBottom: '1px solid #F0ECEE',
                '&:hover': { backgroundColor: '#FFF0F0' },
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{r.organization_name}</Typography>
                  <TierChip tier={r.plan} />
                </Box>
                <Typography sx={{ fontSize: 11.5, color: '#847782', mt: 0.2 }}>
                  {r.cs_owner || t('common.unassigned')} · {t('dashboard.renewals.score', { score: r.score })}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{
                  fontSize: 12.5, mb: 0.4, fontVariantNumeric: 'tabular-nums',
                  color: r.days_until <= 30 ? '#AE5B61' : '#170312',
                  fontWeight: r.days_until <= 30 ? 500 : 400,
                }}>
                  {fmtDate(dayOf(r.renewal_date))}
                  {/* Las del mes en curso que ya pasaron dicen hace cuánto,
                      no un conteo negativo. */}
                  {r.days_until < 0
                    ? ` · ${t('dashboard.renewals.daysAgo', { count: -r.days_until })}`
                    : r.days_until <= 45 ? ` · ${t('common.daysShort', { count: r.days_until })}` : ''}
                </Typography>
                <RenewalChip status={r.renewal_status} />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, tFlag, fmtDate } = useI18n();
  const canLog = useCan('log_activity');
  // El tablero es del equipo entero; sólo administración lo desglosa por
  // persona, que es quien reparte la carga.
  const canFilterOwners = useCan('manage_users');
  const canCloseRenewals = useCan('close_renewals');
  const [closingMonth, setClosingMonth] = useState(false);
  const [busyTask, setBusyTask] = useState(null);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [owners, setOwners] = useState([]);
  const [owner, setOwner] = useState('all');
  const [renewalMonth, setRenewalMonth] = useState(null);

  const loadDashboard = useCallback(() => {
    api.get('/api/dashboard')
      .then((res) => setData(res.data))
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  useEffect(() => {
    if (!canFilterOwners) return;
    api.get('/api/owners').then((res) => setOwners(res.data)).catch(() => setOwners([]));
  }, [canFilterOwners]);

  // Un mismo filtro para las tres columnas: las cuentas se comparan por dueño
  // de CS y las tareas por responsable, que es como se reparte el trabajo.
  const mine = useCallback(
    (ownerId) => (owner === 'all' ? true : owner === 'none' ? !ownerId : ownerId === owner),
    [owner]
  );

  const queue = useMemo(
    () => (data?.contact_today || []).filter((a) => mine(a.cs_owner_id)),
    [data, mine]
  );
  const renewals = useMemo(
    () => (data?.renewals || []).filter((r) => mine(r.cs_owner_id)),
    [data, mine]
  );
  const tasks = useMemo(
    () => (data?.tasks || []).filter((task) => mine(task.assignee_id)),
    [data, mine]
  );
  const contacted = useMemo(
    () => (data?.contacted || []).filter((c) => mine(c.cs_owner_id)),
    [data, mine]
  );

  // Con una persona seleccionada las cifras se recalculan sobre lo filtrado.
  // Las que no dependen del dueño — cartera total, sin asignar, asistencia
  // pendiente — se ocultan en vez de repetir el número global junto a uno
  // filtrado, que se leería como si también fuera de esa persona.
  const stats = useMemo(() => {
    if (!data) return null;
    if (owner === 'all') return data.stats;
    return {
      contact_today: queue.length,
      renewals_90d: renewals.length,
      renewals_confirmed: (data.renewed || []).filter((r) => mine(r.cs_owner_id)).length,
      tasks_open: tasks.length,
      tasks_overdue: tasks.filter((task) => task.overdue).length,
      contacted_30d: contacted.length,
      contacted_responded: contacted.filter((c) => c.responded === true).length,
      contacted_sessions: contacted.filter((c) => c.session_scheduled).length,
    };
  }, [data, owner, mine, queue, renewals, tasks, contacted]);

  // Repuesta tras una edición mía que la borró por accidente: traduce las
  // banderas del backend y cae al score cuando la cuenta no tiene ninguna.
  const reasonFor = (account) =>
    (account.flags && account.flags.length
      ? account.flags.map(tFlag).join(' · ')
      : t('dashboard.contactToday.scoreTrending', { score: account.score }));

  async function completeTask(task) {
    setBusyTask(task.id);
    try {
      await api.patch(`/api/tasks/${task.id}`, { done: true });
      loadDashboard();
    } catch {
      setFailed(true);
    } finally {
      setBusyTask(null);
    }
  }

  const open = (a) => navigate(`/accounts/${a.tenant_id}?source=${a.source_db}`);

  if (failed) return <Layout><Alert severity="error">{t('dashboard.loadError')}</Alert></Layout>;
  if (!data) {
    return (
      <Layout>
        <Skeleton variant="rectangular" height={72} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={420} />
      </Layout>
    );
  }

  // Ventas no trabaja la cola de CS: su tablero son las renovaciones de las
  // cuentas que lleva, y el backend ya no manda nada más.
  if (data.view === 'sales') {
    return (
      <Layout>
        <Paper sx={{ display: 'flex', flexWrap: 'wrap', gap: '1px', backgroundColor: '#E4E0E2', mb: 2, overflow: 'hidden' }}>
          <Stat
            label={t('dashboard.stats.renewals90d')}
            value={data.stats.renewals_90d}
            hint={t('dashboard.stats.renewalsHint', { count: data.stats.renewals_confirmed })}
          />
        </Paper>
        <Box sx={{ maxWidth: 720 }}>
          <RenewalsCard renewals={data.renewals} onOpen={open} />
        </Box>
      </Layout>
    );
  }

  const everyone = owner === 'all';

  return (
    <Layout>
      {(canFilterOwners || canCloseRenewals) && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          {canFilterOwners && <TextField
            select size="small" variant="standard"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            label={t('dashboard.ownerFilter.label')}
            sx={{ minWidth: 220, '& .MuiSelect-select': { fontSize: 13.5, fontFamily: 'Saira' } }}
          >
            <MenuItem value="all" sx={{ fontSize: 13.5, fontWeight: 600 }}>
              {t('dashboard.ownerFilter.all')}
            </MenuItem>
            <MenuItem value="none" sx={{ fontSize: 13.5 }}>{t('common.unassigned')}</MenuItem>
            {owners.map((o) => (
              <MenuItem key={o.id} value={o.id} sx={{ fontSize: 13.5 }}>{o.display_name}</MenuItem>
            ))}
          </TextField>}
          {canCloseRenewals && (
            <Button
              size="small" variant="outlined" onClick={() => setClosingMonth(true)}
              sx={{ ml: 'auto', fontSize: 12 }}
            >{t('dashboard.closeMonth.button')}</Button>
          )}
        </Box>
      )}

      <Paper sx={{ display: 'flex', flexWrap: 'wrap', gap: '1px', backgroundColor: '#E4E0E2', mb: 2, overflow: 'hidden' }}>
        <Stat label={t('dashboard.stats.contactToday')} value={stats.contact_today} />
        {everyone && (
          <Stat
            label={t('dashboard.stats.atRisk')}
            value={stats.at_risk}
            hint={t('dashboard.stats.atRiskHint', { total: stats.total_accounts })}
          />
        )}
        <Stat
          label={t('dashboard.stats.renewals90d')}
          value={stats.renewals_90d}
          hint={t('dashboard.stats.renewalsHint', { count: stats.renewals_confirmed })}
        />
        {data.renewal_kpi && <RenewalRate kpi={data.renewal_kpi} onOpenMonth={setRenewalMonth} />}
        {everyone && <Stat label={t('dashboard.stats.unassigned')} value={stats.unassigned} />}
        {everyone && <Stat label={t('dashboard.stats.awaitingAttendance')} value={stats.awaiting_attendance} />}
        <Stat
          label={t('dashboard.stats.contacted30d')}
          value={stats.contacted_30d}
          hint={t('dashboard.stats.contactedHint', {
            responded: stats.contacted_responded, sessions: stats.contacted_sessions,
          })}
        />
        <Stat
          label={t('tasks.title')}
          value={stats.tasks_open}
          hint={stats.tasks_overdue > 0 ? t('dashboard.tasks.overdue', { count: stats.tasks_overdue }) : undefined}
        />
      </Paper>

      {/* Tres columnas: a quién hay que hablarle, qué se renueva y qué hay
          pendiente. Cada tarjeta guarda su altura natural — estirarlas a la
          más larga dejaba medio tablero en blanco. */}
      <Box sx={{
        display: 'grid', alignItems: 'start', gap: 2,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: '1.5fr 1fr 1fr' },
      }}>
        {/* Primera columna: a quién hablarle hoy y, debajo, a quién ya se le
            habló este mes. */}
        <Box sx={{ display: 'grid', gap: 2, alignContent: 'start', minWidth: 0 }}>
          {/* ---------- contact today ---------- */}
          <Paper sx={{ overflow: 'hidden' }}>
            <CardHeader
              title={t('dashboard.contactToday.title')}
              count={queue.length}
              action={t('dashboard.contactToday.action')}
            />
            {queue.length === 0 && (
              <Box sx={{ p: 3, color: '#847782', fontSize: 14 }}>
                {t('dashboard.contactToday.empty')}
              </Box>
            )}
            {queue.map((a, i) => (
              <Box
                key={`${a.source_db}:${a.tenant_id}`}
                onClick={() => open(a)}
                sx={{
                  display: 'grid', gridTemplateColumns: '24px 1fr auto', gap: 1.5,
                  px: 1.8, py: 1.6, alignItems: 'start', cursor: 'pointer',
                  borderBottom: '1px solid #F0ECEE',
                  backgroundColor: i < 2 ? '#FFF8F8' : 'transparent',
                  '&:hover': { backgroundColor: '#FFF0F0' },
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{
                  fontSize: 15, fontWeight: 600, textAlign: 'right',
                  color: i < 2 ? '#FF6666' : '#847782', fontVariantNumeric: 'tabular-nums',
                }}>{i + 1}</Typography>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 500 }}>{a.organization_name}</Typography>
                    <TierChip tier={a.plan} />
                    <StatusChip band={a.band} />
                  </Box>
                  <Typography sx={{ fontSize: 12.5, color: a.band === 'at_risk' ? '#AE5B61' : '#847782', mt: 0.3 }}>
                    {reasonFor(a)}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#847782', mt: 0.4 }}>
                    {a.days_since_contact == null
                      ? t('dashboard.contactToday.neverContacted')
                      : t('dashboard.contactToday.lastContact', { days: a.days_since_contact })}
                    {' · '}{a.cs_owner || t('common.unassigned').toLowerCase()}
                  </Typography>
                </Box>

                <Button size="small" variant="contained" sx={{ fontSize: 11.5, py: 0.4 }}>
                  {a.cs_owner ? t('dashboard.contactToday.open') : t('dashboard.contactToday.assign')}
                </Button>
              </Box>
            ))}
          </Paper>

          <ContactedCard contacted={contacted} showOwner={everyone} onOpen={open} />
        </Box>

        <RenewalsCard renewals={renewals} onOpen={open} />

        {/* Tareas abiertas del equipo. Se marcan hechas aquí mismo: el tablero
            debe vaciarse conforme se trabaja, no sólo informar. */}
        <Paper sx={{ overflow: 'hidden' }}>
          <CardHeader title={t('tasks.title')} count={tasks.length} />

          {tasks.length === 0 && (
            <Box sx={{ p: 3, color: '#847782', fontSize: 14 }}>{t('dashboard.tasks.empty')}</Box>
          )}

          {tasks.map((task) => (
            <Box
              key={task.id}
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1,
                px: 1.8, py: 1.3, borderBottom: '1px solid #F0ECEE',
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Box sx={{
                width: 3, alignSelf: 'stretch', borderRadius: '2px', flexShrink: 0,
                backgroundColor: task.overdue ? '#AE5B61'
                  : task.days_until != null && task.days_until <= 7 ? '#C9873F' : '#847782',
              }} />
              <Checkbox
                size="small" checked={false} disabled={busyTask === task.id || !canLog}
                onChange={() => completeTask(task)} sx={{ p: 0.4, mt: -0.3 }}
              />
              <Box
                sx={{ minWidth: 0, flexGrow: 1, cursor: 'pointer' }}
                onClick={() => navigate(`/accounts/${task.tenant_id}?source=${task.source_db}`)}
              >
                <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{task.title}</Typography>
                <Typography sx={{ fontSize: 11.5, color: '#847782' }}>
                  {task.account_name}
                  {task.assignee_name ? ` · ${task.assignee_name}` : ''}
                </Typography>
              </Box>
              <Typography sx={{
                fontSize: 11.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                color: task.overdue ? '#AE5B61' : '#847782',
                fontWeight: task.overdue ? 500 : 400, pt: 0.3,
              }}>
                {task.due_date ? fmtDate(dayOf(task.due_date)) : t('tasks.noDate')}
              </Typography>
            </Box>
          ))}
        </Paper>
      </Box>

      <RenewalMonthDialog month={renewalMonth} onClose={() => setRenewalMonth(null)} onOpen={open} />
      <CloseMonthDialog
        open={closingMonth} onClose={() => setClosingMonth(false)}
        onClosed={loadDashboard} onOpen={open}
      />
    </Layout>
  );
}
